import {
  aliasToNamespace,
  modelLabelsFor,
  toNamespaceAlias,
} from '@pigello/construct-i18n';
import type { ContentType } from '@pigello/pigello-matrix';

const EventIdentifierMap: Record<string, string> = {
  'accounts.tenant.userinvited': 'Användare - Hyresgäst inbjuden',
  'accounts.tenant.userhotploginlinkcreated':
    'Användare - Hyresgäst loggade in via länk',
  'accounts.subtenant.userinvited': 'Användare - Kontaktperson inbjuden',
  'accounts.subtenant.userhotploginlinkcreated':
    'Användare - Kontaktperson loggade in via länk',
  'accounts.organizationuser.userinvited':
    'Användare - Systemanvändare inbjuden',
  'accounts.organizationuser.userhotploginlinkcreated':
    'Användare - Systemanvändare loggade in via länk',
  'contracts.apartmentcontract.contractcancellationsigned':
    'Avtal - Lägenhetsavtal uppsägning bekräftad',
  'contracts.apartmentcontract.contractcancelled':
    'Avtal - Lägenhetsavtal uppsagt',
  'contracts.apartmentcontract.contractrenewed':
    'Avtal - Lägenhetsavtal förnyat',
  'contracts.apartmentcontract.contractownerwarning':
    'Avtal - Lägenhetsavtal varning till hyresvärd',
  'contracts.apartmentcontract.contractcounterpartywarning':
    'Avtal - Lägenhetsavtal varning till hyresgäst',
  'contracts.apartmentcontract.contractsigned':
    'Avtal - Lägenhetsavtal signerat',
  'contracts.industrialpremisescontract.contractcancellationsigned':
    'Avtal - Lokalavtal uppsägning bekräftad',
  'contracts.industrialpremisescontract.contractcancelled':
    'Avtal - Lokalavtal uppsagt',
  'contracts.industrialpremisescontract.contractrenewed':
    'Avtal - Lokalavtal förnyat',
  'contracts.industrialpremisescontract.contractownerwarning':
    'Avtal - Lokalavtal varning till hyresvärd',
  'contracts.industrialpremisescontract.contractcounterpartywarning':
    'Avtal - Lokalavtal varning till hyresgäst',
  'contracts.industrialpremisescontract.contractsigned':
    'Avtal - Lokalavtal signerat',
  'contracts.parkingspotcontract.contractcancellationsigned':
    'Avtal - Parkeringsavtal uppsägning bekräftad',
  'contracts.parkingspotcontract.contractcancelled':
    'Avtal - Parkeringsavtal uppsagt',
  'contracts.parkingspotcontract.contractrenewed':
    'Avtal - Parkeringsavtal förnyat',
  'contracts.parkingspotcontract.contractownerwarning':
    'Avtal - Parkeringsavtal varning till hyresvärd',
  'contracts.parkingspotcontract.contractcounterpartywarning':
    'Avtal - Parkeringsavtal varning till hyresgäst',
  'contracts.parkingspotcontract.contractsigned':
    'Avtal - Parkeringsavtal signerat',
  'contracts.outdoorsectioncontract.contractcancellationsigned':
    'Avtal - Utomhussektionsavtal uppsägning bekräftad',
  'contracts.outdoorsectioncontract.contractcancelled':
    'Avtal - Utomhussektionsavtal uppsagt',
  'contracts.outdoorsectioncontract.contractrenewed':
    'Avtal - Utomhussektionsavtal förnyat',
  'contracts.outdoorsectioncontract.contractownerwarning':
    'Avtal - Utomhussektionsavtal varning till hyresvärd',
  'contracts.outdoorsectioncontract.contractcounterpartywarning':
    'Avtal - Utomhussektionsavtal varning till hyresgäst',
  'contracts.outdoorsectioncontract.contractsigned':
    'Avtal - Utomhussektionsavtal signerat',
  'revenue.retroactiveapartmentrevenueproposal.retroactivityproposalapproved':
    'Retrokativitet - Lägenhet, retroaktivt debiteringsförslag godkänt',
  'revenue.retroactiveapartmentrevenueproposal.retroactivityproposaldenied':
    'Retrokativitet - Lägenhet, retroaktivt debiteringsförslag nekat',
  'revenue.retroactiveindustrialpremisesrevenueproposal.retroactivityproposalapproved':
    'Retrokativitet - Lokal, retroaktivt debiteringsförslag godkänt',
  'revenue.retroactiveindustrialpremisesrevenueproposal.retroactivityproposaldenied':
    'Retrokativitet - Lokal, retroaktivt debiteringsförslag nekat',
  'revenue.retroactiveparkingspotrevenueproposal.retroactivityproposalapproved':
    'Retrokativitet - Fordonsplats, retroaktivt debiteringsförslag godkänt',
  'revenue.retroactiveparkingspotrevenueproposal.retroactivityproposaldenied':
    'Retrokativitet - Fordonsplats, retroaktivt debiteringsförslag nekat',
  'revenue.retroactiveoutdoorsectionrevenueproposal.retroactivityproposalapproved':
    'Retrokativitet - Utomhussektion, retroaktivt debiteringsförslag godkänt',
  'revenue.retroactiveoutdoorsectionrevenueproposal.retroactivityproposaldenied':
    'Retrokativitet - Utomhussektion, retroaktivt debiteringsförslag nekat',
  'monitoring.approvalnotation.approvalaccepted':
    'Monitorering - Förfrågan accepterad',
  'monitoring.approvalnotation.approvaldenied':
    'Monitorering - Förfrågan nekad',
  'monitoring.approvalnotation.approvaldelegated':
    'Monitorering - Förfrågan delegerad',
  'monitoring.approvalnotation.approvalwithdrawn':
    'Monitorering - Förfrågan ångrad',
  'monitoring.apartmentmonitoring.pendingapproval':
    'Monitorering - Lägenhetsförslag inväntar godkännande',
  'monitoring.apartmentmonitoring.actionfullyaccepted':
    'Monitorering - Lägenhetsförslag fullt godkänt',
  'monitoring.apartmentmonitoring.actionfullydenied':
    'Monitorering - Lägenhetsförslag fullt nekat',
  'monitoring.apartmentmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Lägenhetsförslag ångrat',
  'monitoring.industrialpremisesmonitoring.pendingapproval':
    'Monitorering - Lokalförslag inväntar godkännande',
  'monitoring.industrialpremisesmonitoring.actionfullyaccepted':
    'Monitorering - Lokalförslag fullt godkänt',
  'monitoring.industrialpremisesmonitoring.actionfullydenied':
    'Monitorering - Lokalförslag fullt nekat',
  'monitoring.industrialpremisesmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Lokalförslag ångrat',
  'monitoring.parkingspotmonitoring.pendingapproval':
    'Monitorering - Fordonsplatsförslag inväntar godkännande',
  'monitoring.parkingspotmonitoring.actionfullyaccepted':
    'Monitorering - Fordonsplatsförslag fullt godkänt',
  'monitoring.parkingspotmonitoring.actionfullydenied':
    'Monitorering - Fordonsplatsförslag fullt nekat',
  'monitoring.parkingspotmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Fordonsplatsförslag ångrat',
  'monitoring.outdoorsectionmonitoring.pendingapproval':
    'Monitorering - Utomhussektionsförslag inväntar godkännande',
  'monitoring.outdoorsectionmonitoring.actionfullyaccepted':
    'Monitorering - Utomhussektionsförslag fullt godkänt',
  'monitoring.outdoorsectionmonitoring.actionfullydenied':
    'Monitorering - Utomhussektionsförslag fullt nekat',
  'monitoring.outdoorsectionmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Utomhussektionsförslag ångrat',
  'monitoring.commonareamonitoring.pendingapproval':
    'Monitorering - Förslag på gemensam yta inväntar godkännande',
  'monitoring.commonareamonitoring.actionfullyaccepted':
    'Monitorering - Förslag på gemensam yta fullt godkänt',
  'monitoring.commonareamonitoring.actionfullydenied':
    'Monitorering - Förslag på gemensam yta fullt nekat',
  'monitoring.commonareamonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på gemensam yta ångrat',
  'monitoring.brfapartmentmonitoring.pendingapproval':
    'Monitorering - Bostadsrättsförslag inväntar godkännande',
  'monitoring.brfapartmentmonitoring.actionfullyaccepted':
    'Monitorering - Bostadsrättsförslag fullt godkänt',
  'monitoring.brfapartmentmonitoring.actionfullydenied':
    'Monitorering - Bostadsrättsförslag fullt nekad',
  'monitoring.brfapartmentmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Bostadsrättsförslag ångrat',
  'monitoring.spacerenovationnotationmonitoring.pendingapproval':
    'Monitorering - Förslag på renovering på enskilt objekt inväntar godkännande',
  'monitoring.spacerenovationnotationmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på renovering på enskilt objekt fullt godkänt',
  'monitoring.spacerenovationnotationmonitoring.actionfullydenied':
    'Monitorering - Förslag på renovering på enskilt objekt fullt nekad',
  'monitoring.spacerenovationnotationmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på renovering på enskilt objekt ångrat',
  'monitoring.structurerenovationnotationmonitoring.pendingapproval':
    'Monitorering - Förslag på renovering på struktur inväntar godkännande',
  'monitoring.structurerenovationnotationmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på renovering på struktur fullt godkänt',
  'monitoring.structurerenovationnotationmonitoring.actionfullydenied':
    'Monitorering - Förslag på renovering på struktur fullt nekad',
  'monitoring.structurerenovationnotationmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på renovering på struktur ångrat',
  'monitoring.taxationrecordmonitoring.pendingapproval':
    'Monitorering - Taxeringsenhetsförslag inväntar godkännande',
  'monitoring.taxationrecordmonitoring.actionfullyaccepted':
    'Monitorering - Taxeringsenhetsförslag fullt godkänt',
  'monitoring.taxationrecordmonitoring.actionfullydenied':
    'Monitorering - Taxeringsenhetsförslag fullt nekad',
  'monitoring.taxationrecordmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Taxeringsenhetsförslag ångrat',
  'monitoring.taxationsubrecordmonitoring.pendingapproval':
    'Monitorering - Värderingsenhetsförslag inväntar godkännande',
  'monitoring.taxationsubrecordmonitoring.actionfullyaccepted':
    'Monitorering - Värderingsenhetsförslag fullt godkänt',
  'monitoring.taxationsubrecordmonitoring.actionfullydenied':
    'Monitorering - Värderingsenhetsförslag fullt nekad',
  'monitoring.taxationsubrecordmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Värderingsenhetsförslag ångrat',
  'monitoring.buildingmonitoring.pendingapproval':
    'Monitorering - Byggnadsförslag inväntar godkännande',
  'monitoring.buildingmonitoring.actionfullyaccepted':
    'Monitorering - Byggnadsförslag fullt godkänt',
  'monitoring.buildingmonitoring.actionfullydenied':
    'Monitorering - Byggnadsförslag fullt nekad',
  'monitoring.buildingmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Byggnadsförslag ångrat',
  'monitoring.parkinglotmonitoring.pendingapproval':
    'Monitorering - Parkeringsområdesförslag inväntar godkännande',
  'monitoring.parkinglotmonitoring.actionfullyaccepted':
    'Monitorering - Parkeringsområdesförslag fullt godkänt',
  'monitoring.parkinglotmonitoring.actionfullydenied':
    'Monitorering - Parkeringsområdesförslag fullt nekad',
  'monitoring.parkinglotmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Parkeringsområdesförslag ångrat',
  'monitoring.outdoorareamonitoring.pendingapproval':
    'Monitorering - Utomhusområdesförslag inväntar godkännande',
  'monitoring.outdoorareamonitoring.actionfullyaccepted':
    'Monitorering - Utomhusområdesförslag fullt godkänt',
  'monitoring.outdoorareamonitoring.actionfullydenied':
    'Monitorering - Utomhusområdesförslag fullt nekad',
  'monitoring.outdoorareamonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Utomhusområdesförslag ångrat',
  'monitoring.realestatemonitoring.pendingapproval':
    'Monitorering - Fastighetsförslag inväntar godkännande',
  'monitoring.realestatemonitoring.actionfullyaccepted':
    'Monitorering - Fastighetsförslag fullt godkänt',
  'monitoring.realestatemonitoring.actionfullydenied':
    'Monitorering - Fastighetsförslag fullt nekad',
  'monitoring.realestatemonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Fastighetsförslag ångrat',
  'monitoring.apartmentrevenuerowmonitoring.pendingapproval':
    'Monitorering - Förslag på debiteringsrad på lägenhet inväntar godkännande',
  'monitoring.apartmentrevenuerowmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på debiteringsrad på lägenhet fullt godkänt',
  'monitoring.apartmentrevenuerowmonitoring.actionfullydenied':
    'Monitorering - Förslag på debiteringsrad på lägenhet fullt nekad',
  'monitoring.apartmentrevenuerowmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på debiteringsrad på lägenhet ångrat',
  'monitoring.apartmentcontractrevenuerowmonitoring.pendingapproval':
    'Monitorering - Förslag på debiteringsrad på lägenhetsavtal inväntar godkännande',
  'monitoring.apartmentcontractrevenuerowmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på debiteringsrad på lägenhetsavtal fullt godkänt',
  'monitoring.apartmentcontractrevenuerowmonitoring.actionfullydenied':
    'Monitorering - Förslag på debiteringsrad på lägenhetsavtal fullt nekad',
  'monitoring.apartmentcontractrevenuerowmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på debiteringsrad på lägenhetsavtal ångrat',
  'monitoring.industrialpremisesrevenuerowmonitoring.pendingapproval':
    'Monitorering - Förslag på debiteringsrad på lokal inväntar godkännande',
  'monitoring.industrialpremisesrevenuerowmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på debiteringsrad på lokal fullt godkänt',
  'monitoring.industrialpremisesrevenuerowmonitoring.actionfullydenied':
    'Monitorering - Förslag på debiteringsrad på lokal fullt nekad',
  'monitoring.industrialpremisesrevenuerowmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på debiteringsrad på lokal ångrat',
  'monitoring.industrialpremisescontractrevenuerowmonitoring.pendingapproval':
    'Monitorering - Förslag på debiteringsrad på lokalavtal inväntar godkännande',
  'monitoring.industrialpremisescontractrevenuerowmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på debiteringsrad på lokalavtal fullt godkänt',
  'monitoring.industrialpremisescontractrevenuerowmonitoring.actionfullydenied':
    'Monitorering - Förslag på debiteringsrad på lokalavtal fullt nekad',
  'monitoring.industrialpremisescontractrevenuerowmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på debiteringsrad på lokalavtal ångrat',
  'monitoring.parkingspotrevenuerowmonitoring.pendingapproval':
    'Monitorering - Förslag på debiteringsrad på fordonsplats inväntar godkännande',
  'monitoring.parkingspotrevenuerowmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på debiteringsrad på fordonsplats fullt godkänt',
  'monitoring.parkingspotrevenuerowmonitoring.actionfullydenied':
    'Monitorering - Förslag på debiteringsrad på fordonsplats fullt nekad',
  'monitoring.parkingspotrevenuerowmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på debiteringsrad på fordonsplats ångrat',
  'monitoring.parkingspotcontractrevenuerowmonitoring.pendingapproval':
    'Monitorering - Förslag på debiteringsrad på parkeringsavtal inväntar godkännande',
  'monitoring.parkingspotcontractrevenuerowmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på debiteringsrad på parkeringsavtal fullt godkänt',
  'monitoring.parkingspotcontractrevenuerowmonitoring.actionfullydenied':
    'Monitorering - Förslag på debiteringsrad på parkeringsavtal fullt nekad',
  'monitoring.parkingspotcontractrevenuerowmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på debiteringsrad på parkeringsavtal ångrat',
  'monitoring.outdoorsectionrevenuerowmonitoring.pendingapproval':
    'Monitorering - Förslag på debiteringsrad på utomhussektion inväntar godkännande',
  'monitoring.outdoorsectionrevenuerowmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på debiteringsrad på utomhussektion fullt godkänt',
  'monitoring.outdoorsectionrevenuerowmonitoring.actionfullydenied':
    'Monitorering - Förslag på debiteringsrad på utomhussektion fullt nekad',
  'monitoring.outdoorsectionrevenuerowmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på debiteringsrad på utomhussektion ångrat',
  'monitoring.outdoorsectioncontractrevenuerowmonitoring.pendingapproval':
    'Monitorering - Förslag på debiteringsrad på utomhussektionsavtal inväntar godkännande',
  'monitoring.outdoorsectioncontractrevenuerowmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på debiteringsrad på utomhussektionsavtal fullt godkänt',
  'monitoring.outdoorsectioncontractrevenuerowmonitoring.actionfullydenied':
    'Monitorering - Förslag på debiteringsrad på utomhussektionsavtal fullt nekad',
  'monitoring.outdoorsectioncontractrevenuerowmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på debiteringsrad på utomhussektionsavtal ångrat',
  'monitoring.brfapartmentrevenuerowmonitoring.pendingapproval':
    'Monitorering - Förslag på debiteringsrad på bostadsrätt inväntar godkännande',
  'monitoring.brfapartmentrevenuerowmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på debiteringsrad på bostadsrätt fullt godkänt',
  'monitoring.brfapartmentrevenuerowmonitoring.actionfullydenied':
    'Monitorering - Förslag på debiteringsrad på bostadsrätt fullt nekad',
  'monitoring.brfapartmentrevenuerowmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på debiteringsrad på bostadsrätt ångrat',
  'monitoring.automaticdistributiontypemonitoring.pendingapproval':
    'Monitorering - Förslag på fördelningsavgift inväntar godkännande',
  'monitoring.automaticdistributiontypemonitoring.actionfullyaccepted':
    'Monitorering - Förslag på fördelningsavgift fullt godkänt',
  'monitoring.automaticdistributiontypemonitoring.actionfullydenied':
    'Monitorering - Förslag på fördelningsavgift fullt nekad',
  'monitoring.automaticdistributiontypemonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på fördelningsavgift ångrat',
  'monitoring.automaticdistributiontypevaluemonitoring.pendingapproval':
    'Monitorering - Förslag på fördelat värde inväntar godkännande',
  'monitoring.automaticdistributiontypevaluemonitoring.actionfullyaccepted':
    'Monitorering - Förslag på fördelat värde fullt godkänt',
  'monitoring.automaticdistributiontypevaluemonitoring.actionfullydenied':
    'Monitorering - Förslag på fördelat värde fullt nekad',
  'monitoring.automaticdistributiontypevaluemonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på fördelat värde ångrat',
  'monitoring.customerearningsrevenuesettingmonitoring.pendingapproval':
    'Monitorering - Inställning för omsättningshyra inväntar godkännande',
  'monitoring.customerearningsrevenuesettingmonitoring.actionfullyaccepted':
    'Monitorering - Inställning för omsättningshyra fullt godkänt',
  'monitoring.customerearningsrevenuesettingmonitoring.actionfullydenied':
    'Monitorering - Inställning för omsättningshyra fullt nekad',
  'monitoring.customerearningsrevenuesettingmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Inställning för omsättningshyra ångrat',
  'monitoring.customerearningsreportmonitoring.pendingapproval':
    'Monitorering - Omsättningshyresrapport inväntar godkännande',
  'monitoring.customerearningsreportmonitoring.actionfullyaccepted':
    'Monitorering - Omsättningshyresrapport fullt godkänt',
  'monitoring.customerearningsreportmonitoring.actionfullydenied':
    'Monitorering - Omsättningshyresrapport fullt nekad',
  'monitoring.customerearningsreportmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Omsättningshyresrapport ångrat',
  'monitoring.indexationtablemonitoring.pendingapproval':
    'Monitorering - Indextabell inväntar godkännande',
  'monitoring.indexationtablemonitoring.actionfullyaccepted':
    'Monitorering - Indextabell fullt godkänt',
  'monitoring.indexationtablemonitoring.actionfullydenied':
    'Monitorering - Indextabell fullt nekad',
  'monitoring.indexationtablemonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Indextabell ångrat',
  'monitoring.indexationtablerowmonitoring.pendingapproval':
    'Monitorering - Förslag på värde i indextabell inväntar godkännande',
  'monitoring.indexationtablerowmonitoring.actionfullyaccepted':
    'Monitorering - Förslag på värde i indextabell fullt godkänt',
  'monitoring.indexationtablerowmonitoring.actionfullydenied':
    'Monitorering - Förslag på värde i indextabell fullt nekad',
  'monitoring.indexationtablerowmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Förslag på värde i indextabell ångrat',
  'monitoring.indexationsettingmonitoring.pendingapproval':
    'Monitorering - Indexuppräkningsinställning inväntar godkännande',
  'monitoring.indexationsettingmonitoring.actionfullyaccepted':
    'Monitorering - Indexuppräkningsinställning fullt godkänt',
  'monitoring.indexationsettingmonitoring.actionfullydenied':
    'Monitorering - Indexuppräkningsinställning fullt nekad',
  'monitoring.indexationsettingmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Indexuppräkningsinställning ångrat',
  'monitoring.indexationsettingusagemonitoring.pendingapproval':
    'Monitorering - Indexuppräkning på debiteringsrad inväntar godkännande',
  'monitoring.indexationsettingusagemonitoring.actionfullyaccepted':
    'Monitorering - Indexuppräkning på debiteringsrad fullt godkänt',
  'monitoring.indexationsettingusagemonitoring.actionfullydenied':
    'Monitorering - Indexuppräkning på debiteringsrad fullt nekad',
  'monitoring.indexationsettingusagemonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Indexuppräkning på debiteringsrad ångrat',
  'monitoring.apartmentcontractmonitoring.pendingapproval':
    'Monitorering - Lägenhetsavtal inväntar godkännande',
  'monitoring.apartmentcontractmonitoring.actionfullyaccepted':
    'Monitorering - Lägenhetsavtal fullt godkänt',
  'monitoring.apartmentcontractmonitoring.actionfullydenied':
    'Monitorering - Lägenhetsavtal fullt nekad',
  'monitoring.apartmentcontractmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Lägenhetsavtal ångrat',
  'monitoring.industrialpremisescontractmonitoring.pendingapproval':
    'Monitorering - Lokalavtal inväntar godkännande',
  'monitoring.industrialpremisescontractmonitoring.actionfullyaccepted':
    'Monitorering - Lokalavtal fullt godkänt',
  'monitoring.industrialpremisescontractmonitoring.actionfullydenied':
    'Monitorering - Lokalavtal fullt nekad',
  'monitoring.industrialpremisescontractmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Lokalavtal ångrat',
  'monitoring.parkingspotcontractmonitoring.pendingapproval':
    'Monitorering - Parkeringsavtal inväntar godkännande',
  'monitoring.parkingspotcontractmonitoring.actionfullyaccepted':
    'Monitorering - Parkeringsavtal fullt godkänt',
  'monitoring.parkingspotcontractmonitoring.actionfullydenied':
    'Monitorering - Parkeringsavtal fullt nekad',
  'monitoring.parkingspotcontractmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Parkeringsavtal ångrat',
  'monitoring.outdoorsectioncontractmonitoring.pendingapproval':
    'Monitorering - Utomhussektionsavtal inväntar godkännande',
  'monitoring.outdoorsectioncontractmonitoring.actionfullyaccepted':
    'Monitorering - Utomhussektionsavtal fullt godkänt',
  'monitoring.outdoorsectioncontractmonitoring.actionfullydenied':
    'Monitorering - Utomhussektionsavtal fullt nekad',
  'monitoring.outdoorsectioncontractmonitoring.pendingapprovaleventwithdrawn':
    'Monitorering - Utomhussektionsavtal ångrat',
  'markets.apartmentlisting.apartmentlistingclosed': 'Annons avslutad',
  'markets.apartmentlistingapplication.apartmentlistingapplicationcreated':
    'Ansökan skapad',
  'markets.apartmentlistingapplication.apartmentlistingapplicationoffersent':
    'Erbjudande skickat',
  'markets.apartmentlistingapplication.apartmentlistingapplicationofferaccepted':
    'Erbjudande accepterat',
  'markets.apartmentlistingapplication.apartmentlistingapplicationdeclined':
    'Ansökan tackad nej till',
  'markets.apartmentlistingapplication.apartmentlistingapplicationrejected':
    'Ansökan avslagen',
  'markets.apartmentlistingapplication.applicationcontractavailabletotenant':
    'Avtal tillgängligt för sökande',
  'markets.apartmentlistingapplication.apartmentlistingapplicantdeclineoverthreshold':
    'Sökande har tackat nej för många gånger',
  'markets.apartmentlistingapplication.apartmentlistingapplicantqueuepublicationlimit':
    'Sökande har nått gränsen för antal ansökningar',
  'markets.apartmentqueueenrollment.blocked': 'Köplats blockerad',
  'contracts.apartmentcontract.apartmentcontractavailabletotenant':
    'Lägenhetsavtal tillgängligt för hyresgäst',
  // For Integrations
  'communication.notification.notificationsent': 'Notis skickad',
  'communication.notificationdistribution.notificationdistributionfailed':
    'Notisutskick misslyckades',
  'accounting.invoicepayment.paymentdistributed': 'Betalning fördelad',
  'accounting.verificationgroup.verificationgroupattested':
    'Verifikationsgrupp attesterad',
  'accounting.invoice.invoiceattestationset': 'Avi/Faktura attesterad',
  'accounting.invoice.invoiceclosed': 'Avi/Faktura stängd',
  'accounting.debtinvoice.debtinvoiceattestationset':
    'Påminnelse/krav attesterad',
  'accounting.debtinvoice.debtinvoiceannulled': 'Påminnelse/krav annullerad',
  'accounts.integrationuseragent.instancecreated': 'Integration skapad',
  'accounts.integrationuseragent.instancedeleted': 'Integration raderad',
  'accounts.integrationuseragent.instanceupdated': 'Integration uppdaterad',
  'accounts.integrationuseragent.useragentcreated': 'Integration skapad',
  'documents.signabledocument.signabledocumentexternalreminderdaysaltered':
    'Signeringsbart dokument extern påminnelse dagar ändrad',
  'documents.signabledocument.signabledocumentexternalsigningstarted':
    'Extern singering påbörjad',
  'documents.signabledocument.signabledocumentsigningaborted':
    'Singering avbruten',
  'documents.signabledocument.signabledocumentsigninginitiated':
    'Singering påbörjad',
  'documents.signabledocument.signabledocumentsigninginitiatedreverted':
    'Påbörjad singering avbruted',
};

// Cron identifiers are manifest keys, and every service declares its own
// handful - 13 distinct keys across all of prod today. Lowercase on purpose:
// the detail log renders them after "Schemalagd körning – ", and crons-card
// capitalises with first-letter:capitalize.
const CronIdentifierMap: Record<string, string> = {
  collect_indexes: 'hämtar index',
  delivery_status: 'uppdaterar leveransstatus',
  fetch_debt_events: 'hämtar påminnelse-/kravhändelser',
  fetch_reference_rate: 'hämtar referensränta',
  get_measurements: 'hämtar mätvärden',
  get_sensors: 'hämtar sensorer',
  process_outbox: 'bearbetar utkorgen',
  process_payments: 'bearbetar betalningar',
  refresh_tokens: 'förnyar åtkomsttoken',
  status_update: 'uppdaterar status',
  transfer_files: 'överför filer',
  verification_retry: 'omförsök av verifikationer',
  verification_unknown_retry: 'omförsök av okända verifikationer',
};

// Falls back to the de-slugged key rather than a raw identifier. Extend the map
// as new crons show up in the manifests.
export function getCronLabel(identifier: string): string {
  return CronIdentifierMap[identifier] ?? identifier.replaceAll('_', ' ');
}

// Generic CRUD events exist for every model (InstanceCreated/Updated/Deleted
// in the backend), so they can't all be enumerated in EventIdentifierMap.
const INSTANCE_EVENT_SUFFIX_LABELS: Record<string, string> = {
  instancecreated: 'skapades',
  instanceupdated: 'uppdaterades',
  instancedeleted: 'raderades',
};
export function getContentTypeVerboseName(
  contentType: string
): string | undefined {
  if (!(toNamespaceAlias(contentType) in aliasToNamespace)) return undefined;
  const verboseName = modelLabelsFor(contentType).verboseName();
  if (!verboseName || verboseName === 'verboseName') return undefined;
  return verboseName;
}

export function getContentTypeVerboseNamePlural(
  contentType: string
): string | undefined {
  if (!(toNamespaceAlias(contentType) in aliasToNamespace)) return undefined;
  const plural = modelLabelsFor(contentType).verboseNamePlural();
  if (!plural || plural === 'verboseNamePlural') return undefined;
  return plural;
}

export function getEventIdentifierLabel(
  identifier: string
): string | undefined {
  const direct = EventIdentifierMap[identifier];
  if (direct) return direct;

  const [appLabel, modelName, eventName] = identifier.split('.');
  const suffixLabel = eventName
    ? INSTANCE_EVENT_SUFFIX_LABELS[eventName]
    : undefined;
  if (!appLabel || !modelName || !suffixLabel) return undefined;

  const verboseName = getContentTypeVerboseName(`${appLabel}.${modelName}`);
  if (!verboseName) return undefined;
  return `${verboseName} ${suffixLabel}`;
}

// An event carries several identifiers; the first one with Swedish copy names it.
export function getFirstEventIdentifierLabel(
  identifiers: string[] | undefined
): string | undefined {
  return identifiers
    ?.map((identifier) => getEventIdentifierLabel(identifier))
    .find(Boolean);
}

export const manualRetryMap: Record<number, string> = {
  0: 'Tillåter ej manuell omkörning',
  1: 'Tillåter manuell omkörning av endast misslyckade jobb',
  2: 'Tillåter manuell omkörning av endast inaktiva jobb',
};

export const cronRetryMap: Record<number, string> = {
  0: 'Automatisk omkörning av jobb görs aldrig',
  1: 'Automatisk omkörning av jobb genomförs',
  2: 'Automatisk omkörning av jobb görs fram till nästa körning',
};

export const ActivationLevelMap: Record<number, ContentType | null> = {
  0: 'sales.organization',
  1: 'organization.company',
  2: 'objects.realestate',
  3: 'accounts.organizationuser',
  666: null,
};
